<template>
  <div class="exam-page">
    <div class="card-detail" v-if="hasExpenses">
      <div class="card-detail-title">Procedimentos agendados</div>
      <div
        class="card-detail-data"
        v-for="(expense, i) in expenses"
        :key="i"
      >{{ expense.expense.code | tuss }} - {{ expense.expense.name }}</div>
    </div>

    <component
      v-for="item in items"
      :is="getComponent(item)"
      :medical-record-id="recordId"
      :patient="patient"
      :data="item"
      :triage="triage"
      @remove="onRemove"
      :key="item.id"
      ref="examForm"
    />

    <div class="document-card exams-chooser">
      <div
        class="exams-chooser-item"
        v-for="exam in exams"
        :key="exam.key"
      >
        <button
          type="button"
          class="btn btn-block"
          :class="exists(exam) ? 'btn-gray-outline' : 'btn-info-outline'"
          @click="add(exam)"
          :disabled="exists(exam)"
        >{{ exam.name }}</button>
      </div>
    </div>

    <div class="record-exam-history" v-if="history.length > 0 && patient?.id">
      <h3 class="previous-records-title">Exames anteriores</h3>

      <search-bar @search="search" />

      <exam-view
        :patient-id="patient.id"
        :id="medicalRecordId"
        :items="filteredHistory"
        :show-edit-button="true"
        @refresh="loadHistory"
      />
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapState } from 'vuex';
import { exams } from './data';
import ExamView from './View.vue';
import SearchBar from '../SearchBar.vue';

export default {
  props: {
    medicalRecordId: {
      type: String,
      required: false,
    },
    patient: {
      type: Object,
      required: true,
    },
    triage: {
      type: Boolean,
      default: false,
    },
    appointmentId: {
      type: String,
      required: false,
    },
    professionalId: {
      type: String,
      required: false,
    },
    insurancePlanId: {
      type: String,
      required: false,
    },
    expenses: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      saving: false,
      loading: false,
      loadingHistory: false,
      recordId: this.medicalRecordId,
      items: [],
      history: [],
      filter: {
        onlyMyRecords: false,
        search: '',
      },
      exams,
    };
  },
  components: {
    SearchBar,
    ExamView,
  },
  mounted() {
    this.init();
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    filteredHistory() {
      const nameMap = {
        galilei: 'Galilei',
        keratoscopy: 'Ceratoscopia',
        oct_macula: 'OCT Macula',
        oct_optic_disc: 'OCT Papila',
        pachymetry: 'Paquimetria',
        visual_acuity: 'Acuidade Visual (P.A.M)',
        retinography: 'Retinografia',
        microscopy: 'Microscopia',
        biometry: 'Biometria',
        visual_field: 'Campo Visual',
        opd: 'OPD',
        retina_mapping: 'Mapeamento de Retina',
      };

      const lowerSearch = new RegExp(this.filter.search.toLowerCase(), 'i');
      return this.history.filter((item) => {
        if (this.filter.onlyMyRecords && item.professional.id !== this.user.id) {
          return false;
        }
        return lowerSearch.test(nameMap[item.templateRenderer]);
      });
    },
    hasExpenses() {
      return this.expenses.length > 0;
    },
    hasItems() {
      return this.items.length > 0;
    },
  },
  methods: {
    async init() {
      await Promise.all([this.load(), this.loadHistory()]);
      this.assignTabIndexes();
    },
    async load() {
      if (!this.recordId) return;

      this.loading = true;
      try {
        const { data } = await this.$http.get(`/medical-records/${this.recordId}/exams`);
        this.items = data.items;
      } catch (e) {
        this.$toast.error(e);
      }
      this.loading = false;
    },
    async loadHistory() {
      if (this.triage) return;

      this.loadingHistory = true;
      try {
        const { data } = await this.$http.get(`/patients/${this.patient.id}/exams`);
        this.history = data.items;
      } catch (e) {
        this.$toast.error(e);
      }
      this.loadingHistory = false;
    },
    search(args) {
      this.filter.onlyMyRecords = args.onlyMyRecords;
      this.filter.search = args.search;
    },
    async add(exam) {
      this.saving = true;
      try {
        if (!this.recordId) {
          await this.createRecord();
        }
        const { data } = await this.$http.post(`/medical-records/${this.recordId}/exams`, {
          templateRenderer: exam.key,
        });
        this.items.push(data);
        this.$nextTick(this.assignTabIndexes);
      } catch (e) {
        this.$toast.error(e);
      }
      this.saving = false;
    },
    async createRecord() {
      const postData = {
        status: 'pending',
        startDate: moment().format('YYYY-MM-DD[T]HH:mm:ss'),
        patientId: this.patient.id,
        professionalId: this.professionalId,
        insurancePlanId: this.insurancePlanId || this.patient.insurancePlanId,
        appointmentId: this.appointmentId,
      };
      const { data } = await this.$http.post('/medical-records', postData);
      this.recordId = data.id;
    },
    exists({ key }) {
      return this.items.some(item => item.templateRenderer === key);
    },
    getComponent({ templateRenderer }) {
      const exam = exams.find(({ key }) => key === templateRenderer);
      return exam.form || null;
    },
    onRemove(id) {
      const index = this.items.findIndex(item => item.id === id);
      if (index !== -1) {
        this.items.splice(index, 1);
      }
    },
    assignTabIndexes() {
      if (!this.hasItems) return;

      let tabIndex = 0;

      this.$refs.examForm.forEach(({ $el }) => {
        const elements = [...$el.querySelectorAll('input,select,textarea,button')];

        elements.sort((a, b) => {
          const aIndex = parseFloat(a.dataset.tabindex) || 999;
          const bIndex = parseFloat(b.dataset.tabindex) || 999;
          return aIndex - bIndex;
        });

        elements.forEach((el) => {
          tabIndex += 1;
          el.setAttribute('tabindex', tabIndex);
        });
      });
    },
  },
};
</script>

<style lang="scss">
@import "../../../../assets/scss/variables";

.exam-page {
  .card-detail {
    background-color: lighten($gray-color-light, 5%);
    border: $border-color solid $border-width;
    border-radius: $border-radius;
    margin-bottom: $layout-spacing-lg;
    padding: $layout-spacing;
    .card-detail-title {
      font-weight: 600;
      margin-bottom: $layout-spacing-sm;
    }
  }

  .document-card {
    background-color: lighten($gray-color, 24%);
    border: $border-width dotted $gray-color;
    border-radius: $border-radius;
    padding: 1.5rem 0;
    text-align: center;
  }
  .exams-chooser {
    bottom: $border-width-lg;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
    grid-gap: $layout-spacing;
    padding: $layout-spacing-lg;
    position: sticky;
    z-index: $zindex-0;
    .exams-chooser-item {
      text-align: center;
    }
  }
  .medical-record-exam {
    margin-bottom: $layout-spacing-lg;
    padding: $layout-spacing-lg;
    background-color: lighten($gray-color-light, 7%);
    border: $border-color solid $border-width;
    border-radius: $border-radius;
    .form-group:last-child {
      margin-bottom: $layout-spacing;
    }
    .upload-area {
      margin-top: 0;
    }
  }

  .entity-files-manager .empty {
    display: none;
  }

  .record-exam-history {
    margin-top: $layout-spacing-lg;
    .eye-exam-card {
      margin-bottom: $layout-spacing-lg;
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}
</style>
